import { Navbar } from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { 
  DollarSign, 
  TrendingUp, 
  PieChart, 
  Calculator, 
  ArrowLeft, 
  CheckCircle,
  CreditCard,
  PiggyBank,
  Wallet
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";

const FinancialAlternatives = () => {
  const { t, language } = useLanguage();

  // Translated financial options data
  const financialOptions = [
    {
      id: 1,
      icon: DollarSign,
      title: t('financialAlternatives.options.crowdfunding.title'),
      description: t('financialAlternatives.options.crowdfunding.description'),
      features: [
        t('financialAlternatives.options.crowdfunding.features.0'),
        t('financialAlternatives.options.crowdfunding.features.1'),
        t('financialAlternatives.options.crowdfunding.features.2'),
        t('financialAlternatives.options.crowdfunding.features.3')
      ]
    },
    {
      id: 2,
      icon: CreditCard,
      title: t('financialAlternatives.options.loans.title'),
      description: t('financialAlternatives.options.loans.description'),
      features: [
        t('financialAlternatives.options.loans.features.0'),
        t('financialAlternatives.options.loans.features.1'),
        t('financialAlternatives.options.loans.features.2'),
        t('financialAlternatives.options.loans.features.3')
      ]
    },
    {
      id: 3,
      icon: PieChart,
      title: t('financialAlternatives.options.investment.title'),
      description: t('financialAlternatives.options.investment.description'),
      features: [
        t('financialAlternatives.options.investment.features.0'),
        t('financialAlternatives.options.investment.features.1'),
        t('financialAlternatives.options.investment.features.2'),
        t('financialAlternatives.options.investment.features.3')
      ]
    },
    {
      id: 4,
      icon: PiggyBank,
      title: t('financialAlternatives.options.savings.title'),
      description: t('financialAlternatives.options.savings.description'),
      features: [
        t('financialAlternatives.options.savings.features.0'),
        t('financialAlternatives.options.savings.features.1'),
        t('financialAlternatives.options.savings.features.2'),
        t('financialAlternatives.options.savings.features.3')
      ]
    }
  ];

  // Translated benefits data
  const benefits = [
    {
      icon: CheckCircle,
      title: t('financialAlternatives.benefits.custom.title'),
      description: t('financialAlternatives.benefits.custom.description')
    },
    {
      icon: TrendingUp,
      title: t('financialAlternatives.benefits.growth.title'),
      description: t('financialAlternatives.benefits.growth.description')
    },
    {
      icon: Calculator,
      title: t('financialAlternatives.benefits.transparency.title'),
      description: t('financialAlternatives.benefits.transparency.description')
    }
  ];

  return (
    <div className="min-h-screen" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="py-20 bg-hero-gradient text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <span className="text-sm font-semibold text-primary-foreground uppercase tracking-wide animate-fade-in-down" style={{ animationDelay: '0.1s' }}>
                {t('financialAlternatives.hero.sectionTitle')}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mt-3 mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                {t('financialAlternatives.hero.title')}
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                {t('financialAlternatives.hero.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit) => (
                <div key={benefit.title} className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Financial Options */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('financialAlternatives.options.title')}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t('financialAlternatives.options.subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {financialOptions.map((option) => (
                <div 
                  key={option.id} 
                  className="bg-card rounded-2xl p-8 shadow-card hover:shadow-elevated transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-hero-gradient flex items-center justify-center flex-shrink-0">
                      <option.icon className="w-7 h-7 text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{option.title}</h3>
                      <p className="text-muted-foreground">{option.description}</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {option.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button className="w-full gap-2 bg-hero-gradient hover:opacity-90 text-primary-foreground">
                    {t('common.learnMore')}
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-sidebar text-sidebar-foreground">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-2xl mx-auto">
              <Wallet className="w-16 h-16 text-sidebar-primary mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('financialAlternatives.cta.title')}</h2>
              <p className="text-sidebar-foreground/70 mb-8 text-lg">
                {t('financialAlternatives.cta.description')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button size="lg" className="bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 gap-2">
                  {t('financialAlternatives.cta.consult')}
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <Button size="lg" variant="outline" className="border-sidebar-border text-sidebar-foreground hover:bg-sidebar-accent gap-2">
                  {t('financialAlternatives.cta.calculate')}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FinancialAlternatives;